import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    ArrowLeft, 
    Clock, 
    AlertTriangle, 
    AlertCircle, 
    CheckCircle,
    Trash2,
    ChevronRight,
    History as HistoryIcon
} from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urgencyConfig = {
    EMERGENCY: { 
        icon: AlertTriangle, 
        class: 'urgency-emergency', 
        label: 'emergency'
    },
    URGENT: { 
        icon: AlertCircle, 
        class: 'urgency-urgent', 
        label: 'urgent'
    },
    ROUTINE: { 
        icon: CheckCircle, 
        class: 'urgency-routine', 
        label: 'routine'
    }
};

export default function History() {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [checks, setChecks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchHistory();
    }, []);

    const fetchHistory = async () => {
        try {
            const response = await axios.get(`${API}/symptoms/history`);
            setChecks(response.data);
        } catch (error) {
            console.error('Failed to fetch history:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        setDeleting(true);
        try {
            await axios.delete(`${API}/symptoms/${deleteId}`);
            setChecks(prev => prev.filter(c => c.id !== deleteId));
        } catch (error) {
            console.error('Failed to delete:', error);
        } finally {
            setDeleting(false);
            setDeleteId(null);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return t('today') || 'Today';
        if (diffDays === 1) return t('yesterday') || 'Yesterday';
        if (diffDays < 7) return `${diffDays} ${t('daysAgo') || 'days ago'}`;
        
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="history-page">
            <div className="max-w-4xl mx-auto px-4">
                {/* Header */}
                <div className="mb-8">
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate('/dashboard')}
                        className="mb-4"
                        data-testid="back-to-dashboard"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        {t('backToDashboard') || 'Back to Dashboard'}
                    </Button>
                    <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">
                        {t('assessmentHistory') || 'Assessment History'}
                    </h1>
                    <p className="text-muted-foreground">
                        {t('viewAndManage') || 'View and manage your past symptom assessments'}
                    </p>
                </div>

                {/* Content */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <div>
                            <CardTitle className="font-heading">{t('allAssessments') || 'All Assessments'}</CardTitle>
                            <CardDescription>
                                {checks.length} {checks.length !== 1 ? t('totalAssessments') || 'total assessments' : t('totalAssessment') || 'total assessment'}
                            </CardDescription>
                        </div>
                        <Link to="/symptom-check">
                            <Button className="rounded-full" data-testid="new-check-btn">
                                {t('newCheck') || 'New Check'}
                            </Button>
                        </Link>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <div className="spinner" />
                            </div>
                        ) : checks.length === 0 ? (
                            <div className="text-center py-12">
                                <HistoryIcon className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                                <h3 className="font-heading text-xl font-semibold mb-2">{t('noAssessmentsYet') || 'No assessments yet'}</h3>
                                <p className="text-muted-foreground mb-6">
                                    {t('startFirstSymptomCheck') || 'Start your first symptom check to see your history here'}
                                </p>
                                <Link to="/symptom-check">
                                    <Button className="rounded-full" data-testid="empty-state-btn">
                                        {t('startFirstAssessment') || 'Start First Assessment'}
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {checks.map((check, index) => {
                                    const config = urgencyConfig[check.urgency_level] || urgencyConfig.ROUTINE;
                                    const Icon = config.icon;
                                    
                                    return (
                                        <div 
                                            key={check.id}
                                            className="group relative flex items-center justify-between p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-muted/50 transition-colors"
                                            style={{ animationDelay: `${index * 0.05}s` }}
                                            data-testid={`history-item-${index}`}
                                        >
                                            <Link 
                                                to={`/diagnosis/${check.id}`}
                                                className="flex items-center gap-4 flex-1"
                                            >
                                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.class}`}>
                                                    <Icon className="w-6 h-6" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-1">
                                                        <p className="font-medium truncate">
                                                            {check.symptoms.slice(0, 3).join(', ')}
                                                            {check.symptoms.length > 3 && ` +${check.symptoms.length - 3}`}
                                                        </p>
                                                        <Badge className={`${config.class} flex-shrink-0`}>
                                                            {t(config.label)}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {formatDate(check.created_at)}
                                                        </span>
                                                        <span>{formatTime(check.created_at)}</span>
                                                        <span className="hidden sm:inline">
                                                            {check.body_parts.join(', ')}
                                                        </span>
                                                    </div>
                                                </div>
                                                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                                            </Link>
                                            
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="ml-2 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setDeleteId(check.id);
                                                }}
                                                data-testid={`delete-btn-${index}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent data-testid="delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('deleteAssessment') || 'Delete Assessment'}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('areYouSureDelete') || 'Are you sure you want to delete this assessment? This action cannot be undone.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-cancel-btn">{t('cancel')}</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDelete}
                            className="bg-destructive hover:bg-destructive/90"
                            disabled={deleting}
                            data-testid="delete-confirm-btn"
                        >
                            {deleting ? t('deleting') || 'Deleting...' : t('delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
